import { useEffect, useMemo, useState } from 'react';
import {
  deactivateMember,
  fetchMemberCandidates,
  fetchMembers,
  updateMemberRoles,
  upsertMember,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import './MembersPage.css';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member', hint: 'สแกน / COA' },
  { value: 'admin', label: 'Admin', hint: 'ดูแลระบบ' },
  { value: 'developer', label: 'Developer', hint: 'สิทธิ์เต็ม' },
];

function initials(name, username) {
  const raw = String(name || username || '?').trim();
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase() || '?';
}

function roleTone(role) {
  if (role === 'developer') return 'dev';
  if (role === 'admin') return 'admin';
  return 'member';
}

export default function MembersPage() {
  const { user, hasRole } = useAuth();
  const canAssignDeveloper = hasRole('developer');
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [members, cand] = await Promise.all([fetchMembers(), fetchMemberCandidates()]);
      setRows(members || []);
      setCandidates(cand || []);
    } catch (err) {
      setError(err.message || 'โหลดสมาชิกไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const roleChoices = useMemo(
    () => ROLE_OPTIONS.filter((r) => canAssignDeveloper || r.value !== 'developer'),
    [canAssignDeveloper]
  );

  const activeCount = rows.filter((r) => r.isActive).length;

  async function handleAdd(e) {
    e.preventDefault();
    if (!addUserId) return;
    setBusyId('add');
    try {
      await upsertMember({ userId: Number(addUserId), roles: [addRole] });
      setAddUserId('');
      setAddRole('member');
      await load();
    } catch (err) {
      setError(err.message || 'เพิ่มสมาชิกไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRoleChange(row, role) {
    setBusyId(row.userId);
    try {
      await updateMemberRoles(row.userId, [role]);
      await load();
    } catch (err) {
      setError(err.message || 'เปลี่ยนบทบาทไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeactivate(row) {
    if (!window.confirm(`ปิดสิทธิ์ "${row.name || row.username}" ?`)) return;
    setBusyId(row.userId);
    try {
      await deactivateMember(row.userId);
      await load();
    } catch (err) {
      setError(err.message || 'ปิดสิทธิ์ไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="members-page stack">
      <header className="data-hero">
        <div>
          <p className="data-kicker">Access Control</p>
          <h1>สมาชิก Rollscan</h1>
          <p className="muted data-sub">
            เฉพาะ Developer และ Admin — Member มองไม่เห็นหน้านี้
            {!canAssignDeveloper ? ' · Admin จัดการ Dev ไม่ได้' : ''}
          </p>
        </div>
        <div className="members-hero-meta">
          <span className="members-chip">{activeCount} สมาชิกใช้งาน</span>
        </div>
      </header>

      <section className="card members-panel">
        <div className="members-panel-head">
          <div>
            <h2>เพิ่มสมาชิก</h2>
            <p className="muted">เลือกจากผู้ที่มีสิทธิ์เข้าแอปใน Center</p>
          </div>
        </div>
        <form className="members-add-form" onSubmit={handleAdd}>
          <label className="members-field">
            <span>ผู้ใช้</span>
            <select
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
              required
              aria-label="เลือกผู้ใช้"
            >
              <option value="">เลือกผู้ใช้…</option>
              {candidates.map((c) => (
                <option key={c.userId} value={c.userId}>
                  {c.name} ({c.username})
                </option>
              ))}
            </select>
          </label>
          <label className="members-field">
            <span>บทบาท</span>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              aria-label="บทบาท"
            >
              {roleChoices.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} — {r.hint}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-primary members-add-btn"
            type="submit"
            disabled={!addUserId || busyId === 'add'}
          >
            {busyId === 'add' ? 'กำลังเพิ่ม…' : 'เพิ่มสมาชิก'}
          </button>
        </form>
        {candidates.length === 0 ? (
          <p className="members-hint muted">
            ไม่มีผู้ใช้ใหม่จาก Center — ทุกคนถูกเพิ่มแล้ว หรือยังไม่ถูก grant ROLLSCAN
          </p>
        ) : null}
      </section>

      {error ? <p className="members-error">{error}</p> : null}
      {loading ? <p className="muted">กำลังโหลด...</p> : null}

      <section className="card members-panel members-list-panel">
        <div className="members-panel-head">
          <div>
            <h2>รายชื่อสมาชิก</h2>
            <p className="muted">
              {canAssignDeveloper
                ? 'เปลี่ยนบทบาทหรือปิดสิทธิ์ในแอป'
                : 'จัดการ Member / Admin ได้ — บัญชี Developer แก้ไม่ได้'}
            </p>
          </div>
        </div>

        <ul className="members-list">
          {rows.map((row) => {
            const primaryRole = row.roles?.[0] || 'member';
            const isSelf = user?.id && Number(user.id) === Number(row.userId);
            const isDeveloperRow = (row.roles || []).includes('developer');
            const canEditRow =
              !isSelf && row.isActive && (canAssignDeveloper || !isDeveloperRow);
            const tone = roleTone(primaryRole);
            return (
              <li
                key={row.id}
                className={`members-row${!row.isActive ? ' is-inactive' : ''}`}
              >
                <div className="members-identity">
                  <span className={`members-avatar tone-${tone}`} aria-hidden="true">
                    {initials(row.name, row.username)}
                  </span>
                  <div className="members-copy">
                    <strong>{row.name}</strong>
                    <div className="members-meta">
                      <span>{row.username}</span>
                      {isSelf ? <span className="members-you">คุณ</span> : null}
                      {!row.isActive ? <span className="members-off">ปิดอยู่</span> : null}
                    </div>
                  </div>
                </div>

                <div className="members-actions">
                  <span className={`members-role-pill is-visible tone-${tone}`}>{primaryRole}</span>
                  {isSelf ? (
                    <span className="members-self-note muted">แก้บทบาทตัวเองไม่ได้</span>
                  ) : null}
                  {!isSelf && isDeveloperRow && !canAssignDeveloper ? (
                    <span className="members-self-note muted">Admin จัดการ Dev ไม่ได้</span>
                  ) : null}
                  {canEditRow ? (
                    <select
                      className="members-role-select"
                      value={primaryRole}
                      disabled={busyId === row.userId}
                      onChange={(e) => handleRoleChange(row, e.target.value)}
                      aria-label={`บทบาทของ ${row.name}`}
                    >
                      {roleChoices.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {canEditRow ? (
                    <button
                      type="button"
                      className="btn members-off-btn"
                      disabled={busyId === row.userId}
                      onClick={() => handleDeactivate(row)}
                    >
                      ปิดสิทธิ์
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>

        {!loading && rows.length === 0 ? (
          <p className="members-empty muted">ยังไม่มีสมาชิก</p>
        ) : null}
      </section>
    </div>
  );
}
